import { TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, IconButton, TextField, Box } from "@mui/material";
import React, { useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { ComponentModal, IProduct, IGrillingConfig } from "../common/interfaces";
import { usePosStore } from "../context/posStore";
import { useModalStore } from "../context/modalStore";
import { productsService } from "../services/productsService";
import { CommonService } from "../services/CommonService";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { v4 } from "uuid";
import { getLinePrice, isSameLine } from "../services/pricing";
import { IProductOptionsResult } from "./modal/productOptions";

const MySwal = withReactContent(Swal);

// Cantidad mínima permitida (50 gramos = 0.05 kg)
const MIN_QTY = 0.05;

export const CartView: React.FC = () => {
  const messagesEndRef = React.useRef<HTMLTableElement>(null);
  const products = usePosStore((state) => state.products)
  const setProducts = usePosStore((state) => state.setProducts)
  const modalManager = useModalStore()

  useEffect(() => {
    scrollToBottom();
  }, [products])

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView(false)

  const deleteItem = (row: IProduct): void => {
    modalManager.openModal(ComponentModal.CancelItem, {item: row})
  };

  const handleGrillingServiceConfirm = async (row: IProduct, config: IGrillingConfig): Promise<void> => {
    console.log('🔧 handleGrillingServiceConfirm llamado:', {
      product: row.name,
      config,
      has_grilling_service: row.has_grilling_service
    });

    const quantity = Number(row.quantity);

    try {
      // Si el producto tiene has_grilling_service=true, el servicio va GRATIS
      // Solo actualizar la configuración, NO agregar línea de servicio, NO necesitamos llamar al backend
      if (row.has_grilling_service) {
        console.log('🔧 Servicio GRATIS - Solo actualizando configuración (sin llamar backend)');
        const updatedProducts = products.map((p: IProduct) => {
          if (isSameLine(p.line_uid, row.line_uid)) {
            return {
              ...p,
              grilling_config: config
            };
          }
          return p;
        });
        setProducts(updatedProducts);
        CommonService.toast('Servicio de asado activado (gratis)', 'success');
        return;
      }

      // Si NO tiene has_grilling_service, necesitamos obtener info del servicio y agregar línea con costo
      console.log('🔧 Obteniendo info del servicio de asado...');
      const serviceInfo = await productsService.getGrillingServiceInfo(row.id, quantity);
      console.log('🔧 Respuesta del servicio:', serviceInfo.data);

      if (serviceInfo.data?.grilling_service) {
        const grillingService = serviceInfo.data.grilling_service;
        console.log('🔧 Servicio de asado encontrado:', grillingService);
        console.log('🔧 Servicio CON COSTO - Agregando línea de servicio');

        // Crear producto de servicio de asado con propiedades mínimas requeridas
        const grillingProduct = {
          id: grillingService.id,
          name: grillingService.name,
          price_1: grillingService.price_per_kg,
          quantity: quantity,
          is_grilling_service: true,
          parent_product_id: row.id,
          parent_line_uid: row.line_uid,
          line_uid: v4(),
          force_kitchen: true,
          // Propiedades requeridas por IProduct con valores por defecto
          description: '',
          image: null,
          type: 'service',
          min_stock: '0',
          max_stock: '0',
          product_loss_percentage: '0',
          department_id: 0,
          product_type_id: 0,
          cost: '0',
          price_type: 0,
          price_2: 0,
          price_3: 0,
          price_4: 0,
          price_5: 0,
          price_iva: 0,
          default_price: null,
          book_id: null,
          iva_id: null,
          ieps_id: null,
          unit_id: null,
          supplier_id: 0,
          barcode: '',
          alt_code: '',
          fiscal_unit_id: '',
          fiscal_prod_serv_id: '',
          hidden: '0',
          is_active: null,
          is_bulk: '0',
          created_by: '',
          edited_by: null,
          created_at: new Date(),
          updated_at: new Date(),
          deleted_at: null,
          inventory: []
        } as IProduct;

        // Actualizar producto original con la configuración e insertar servicio justo después
        const updatedProducts: IProduct[] = [];
        products.forEach((p: IProduct) => {
          if (isSameLine(p.line_uid, row.line_uid)) {
            // Agregar producto padre actualizado
            updatedProducts.push({
              ...p,
              grilling_config: config
            });
            // Agregar servicio de asado inmediatamente después
            updatedProducts.push(grillingProduct);
          } else {
            updatedProducts.push(p);
          }
        });

        console.log('🔧 Productos actualizados:', updatedProducts.length);
        console.log('🔧 Lista de productos:', updatedProducts.map(p => ({
          id: p.id,
          name: p.name,
          is_grilling_service: p.is_grilling_service,
          parent_product_id: p.parent_product_id
        })));
        setProducts(updatedProducts);
        CommonService.toast('Servicio de asado activado', 'success');
      }
    } catch (error) {
      console.error('Error al activar servicio de asado:', error);
      CommonService.toast('Error al activar servicio de asado', 'error');
    }
  };

  /**
   * Reabre la ventana de opciones sobre un renglon ya agregado. Reusa el mismo componente
   * que al agregar, sembrado con lo que el renglon ya traia.
   */
  const editProductOptions = (row: IProduct): void => {
    modalManager.openModal(ComponentModal.ProductOptions, {
      product: row,
      mode: 'edit',
      quantity: Number(row.quantity ?? 1),
      initialSelections: row.selected_option_items ?? [],
      initialLegacy: row.selectedOptions ?? '',
      onConfirm: (results: IProductOptionsResult[]) => {
        setProducts(
          products.flatMap((product: IProduct) => {
            if (!isSameLine(product.line_uid, row.line_uid)) return [product];

            // Si la cajera repartio el renglon (el cliente cambio de opinion con una de las
            // cinco), sale mas de uno. El PRIMERO conserva el line_uid original para no
            // desprender el servicio de asado ni ninguna otra linea hija que lo apunte.
            return results.map((result, index) => ({
              ...product,
              line_uid: index === 0 ? product.line_uid : v4(),
              quantity: result.quantity,
              selectedOptions: result.selectedOptions,
              selected_option_items: result.selected_option_items,
            }));
          })
        );
        modalManager.closeModal();
      },
    });
  };

  const editGrillingOptions = (parentProduct: IProduct): void => {
    console.log('✏️ Editando opciones de asado para:', parentProduct.name);

    const currentConfig = parentProduct.grilling_config;

    MySwal.fire({
      title: "✏️ Editar Opciones de Asado",
      html: `
        <div style="text-align: left; padding: 20px; max-width: 500px; margin: 0 auto;">
          <div style="margin-bottom: 24px;">
            <label style="display: block; margin-bottom: 8px; font-weight: 600; font-size: 14px; color: #e0e0e0;">
              Punto de cocción:
            </label>
            <select
              id="cooking-point"
              class="swal2-input"
              style="width: 100%; margin: 0; padding: 10px; font-size: 15px; border-radius: 8px; border: 2px solid #444; background: #2a2a2a; color: #fff;"
            >
              <option value="TERMINO MEDIO" ${currentConfig?.cooking_point === 'TERMINO MEDIO' ? 'selected' : ''}>Término Medio</option>
              <option value="3/4" ${currentConfig?.cooking_point === '3/4' ? 'selected' : ''}>3/4</option>
              <option value="BIEN COCIDO" ${currentConfig?.cooking_point === 'BIEN COCIDO' ? 'selected' : ''}>Bien Cocido</option>
            </select>
          </div>

          <div style="margin-bottom: 24px;">
            <label style="display: block; margin-bottom: 8px; font-weight: 600; font-size: 14px; color: #e0e0e0;">
              Tipo de corte:
            </label>
            <select
              id="cut-type"
              class="swal2-input"
              style="width: 100%; margin: 0; padding: 10px; font-size: 15px; border-radius: 8px; border: 2px solid #444; background: #2a2a2a; color: #fff;"
            >
              <option value="EN TIRAS" ${currentConfig?.cut_type === 'EN TIRAS' ? 'selected' : ''}>En Tiras</option>
              <option value="ENTERA" ${currentConfig?.cut_type === 'ENTERA' ? 'selected' : ''}>Entera</option>
              <option value="PICADA PARA TACO" ${currentConfig?.cut_type === 'PICADA PARA TACO' ? 'selected' : ''}>Picada para Taco</option>
            </select>
          </div>

          <div>
            <label style="display: block; margin-bottom: 8px; font-weight: 600; font-size: 14px; color: #e0e0e0;">
              📝 Notas para cocina (opcional):
            </label>
            <textarea
              id="kitchen-notes"
              placeholder="Ej: Sin sal, bien dorado, etc."
              style="width: 100%; margin: 0; padding: 10px; font-size: 14px; border-radius: 8px; border: 2px solid #444; background: #2a2a2a; color: #fff; min-height: 70px; max-height: 120px; resize: vertical; font-family: inherit; box-sizing: border-box;"
            >${currentConfig?.kitchen_notes || ''}</textarea>
          </div>
        </div>
      `,
      width: '600px',
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ff6f00',
      cancelButtonColor: '#666',
      customClass: {
        popup: 'grilling-modal',
        confirmButton: 'grilling-confirm-btn',
        cancelButton: 'grilling-cancel-btn'
      },
      preConfirm: () => {
        const cookingPoint = (document.getElementById('cooking-point') as HTMLSelectElement).value;
        const cutType = (document.getElementById('cut-type') as HTMLSelectElement).value;
        const kitchenNotes = (document.getElementById('kitchen-notes') as HTMLTextAreaElement).value.trim();
        return { cookingPoint, cutType, kitchenNotes };
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedConfig: IGrillingConfig = {
          has_grilling_service: currentConfig?.has_grilling_service ?? true,
          has_complements: currentConfig?.has_complements ?? false,
          complements_quantity: currentConfig?.complements_quantity ?? 0,
          cooking_point: result.value.cookingPoint,
          cut_type: result.value.cutType,
          kitchen_notes: result.value.kitchenNotes || undefined
        };

        // Actualizar la configuración del producto padre
        const updatedProducts = products.map((p: IProduct) => {
          if (isSameLine(p.line_uid, parentProduct.line_uid)) {
            return {
              ...p,
              grilling_config: updatedConfig
            };
          }
          return p;
        });

        setProducts(updatedProducts);
        CommonService.toast('Opciones de asado actualizadas', 'success');
      }
    });
  };

  const toggleGrillingService = async (row: IProduct): Promise<void> => {
    console.log('🔥 toggleGrillingService llamado:', {
      product: row.name,
      product_type_id: row.product_type_id,
      grilling_config: row.grilling_config
    });

    const currentlyActive = row.grilling_config?.has_grilling_service || false;
    const quantity = Number(row.quantity);
    const isCarnesProduct = row.product_type_id === 41;

    console.log('🔥 Estado:', { currentlyActive, quantity, isCarnesProduct });

    try {
      if (!currentlyActive) {
        console.log('🔥 ACTIVANDO servicio de asado');

        // Si es producto CARNES, abrir modal para preguntar cocción y corte
        if (isCarnesProduct) {
          console.log('🔥 Abriendo modal para producto CARNES');
          MySwal.fire({
            title: "🔥 Opciones de Asado",
            html: `
              <div style="text-align: left; padding: 20px; max-width: 500px; margin: 0 auto;">
                <div style="margin-bottom: 24px;">
                  <label style="display: block; margin-bottom: 8px; font-weight: 600; font-size: 14px; color: #e0e0e0;">
                    Punto de cocción:
                  </label>
                  <select
                    id="cooking-point"
                    class="swal2-input"
                    style="width: 100%; margin: 0; padding: 10px; font-size: 15px; border-radius: 8px; border: 2px solid #444; background: #2a2a2a; color: #fff;"
                  >
                    <option value="TERMINO MEDIO">Término Medio</option>
                    <option value="3/4">3/4</option>
                    <option value="BIEN COCIDO">Bien Cocido</option>
                  </select>
                </div>

                <div style="margin-bottom: 24px;">
                  <label style="display: block; margin-bottom: 8px; font-weight: 600; font-size: 14px; color: #e0e0e0;">
                    Tipo de corte:
                  </label>
                  <select
                    id="cut-type"
                    class="swal2-input"
                    style="width: 100%; margin: 0; padding: 10px; font-size: 15px; border-radius: 8px; border: 2px solid #444; background: #2a2a2a; color: #fff;"
                  >
                    <option value="EN TIRAS">En Tiras</option>
                    <option value="ENTERA">Entera</option>
                    <option value="PICADA PARA TACO">Picada para Taco</option>
                  </select>
                </div>

                <div>
                  <label style="display: block; margin-bottom: 8px; font-weight: 600; font-size: 14px; color: #e0e0e0;">
                    📝 Notas para cocina (opcional):
                  </label>
                  <textarea
                    id="kitchen-notes"
                    placeholder="Ej: Sin sal, bien dorado, etc."
                    style="width: 100%; margin: 0; padding: 10px; font-size: 14px; border-radius: 8px; border: 2px solid #444; background: #2a2a2a; color: #fff; min-height: 70px; max-height: 120px; resize: vertical; font-family: inherit; box-sizing: border-box;"
                  ></textarea>
                </div>
              </div>
            `,
            width: '600px',
            showCancelButton: true,
            confirmButtonText: 'Activar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#ff6f00',
            cancelButtonColor: '#666',
            customClass: {
              popup: 'grilling-modal',
              confirmButton: 'grilling-confirm-btn',
              cancelButton: 'grilling-cancel-btn'
            },
            preConfirm: () => {
              const cookingPoint = (document.getElementById('cooking-point') as HTMLSelectElement).value;
              const cutType = (document.getElementById('cut-type') as HTMLSelectElement).value;
              const kitchenNotes = (document.getElementById('kitchen-notes') as HTMLTextAreaElement).value.trim();
              return { cookingPoint, cutType, kitchenNotes };
            }
          }).then(async (result) => {
            if (result.isConfirmed) {
              const config: IGrillingConfig = {
                has_grilling_service: true,
                has_complements: false,
                complements_quantity: 0,
                cooking_point: result.value.cookingPoint,
                cut_type: result.value.cutType,
                kitchen_notes: result.value.kitchenNotes || undefined
              };

              console.log('🔥 Config a aplicar:', config);
              await handleGrillingServiceConfirm(row, config);
            }
          });
          return;
        }

        // Para productos NO CARNES, activar automáticamente sin preguntar
        const config: IGrillingConfig = {
          has_grilling_service: true,
          has_complements: false,
          complements_quantity: 0
        };

        console.log('🔥 Config a aplicar (NO CARNES):', config);
        await handleGrillingServiceConfirm(row, config);
      } else {
        console.log('🔥 DESACTIVANDO servicio de asado');

        // DESACTIVAR: Remover línea de servicio de asado y actualizar config
        const deactivatedConfig: IGrillingConfig = {
          has_grilling_service: false,
          has_complements: false,
          complements_quantity: 0
        };

        const updatedProducts = products
          .filter((p: IProduct) => {
            // Remover el servicio de asado asociado a este producto
            if (p.is_grilling_service && isSameLine(p.parent_line_uid, row.line_uid)) {
              return false;
            }
            return true;
          })
          .map((p: IProduct) => {
            // Actualizar configuración del producto original
            if (isSameLine(p.line_uid, row.line_uid)) {
              return {
                ...p,
                grilling_config: deactivatedConfig
              };
            }
            return p;
          });

        setProducts(updatedProducts);
        CommonService.toast('Servicio de asado desactivado', 'success');
      }

    } catch (error) {
      console.error('Error toggling grilling service:', error);
      CommonService.toast('Error al actualizar servicio de asado', 'error');
    }
  };

  return (
    <TableContainer component={Paper} style={{ maxHeight: "600px" }}>
      <Table
        sx={{ minWidth: "80%" }}
        aria-label="simple table"
        style={{ backgroundColor: "#333", color: "#fff" }}
        ref={messagesEndRef}
      >
        <TableHead>
          <TableRow>
            <TableCell width={"10%"}></TableCell>
            <TableCell align="left" width={"20%"}>
              Cantidad
            </TableCell>
            <TableCell width={"30%"}>Producto</TableCell>
            <TableCell width={"10%"}>Precio</TableCell>
            <TableCell align="right" width={"10%"}>
              IVA
            </TableCell>
            <TableCell align="right" width={"10%"}>
              IEPS
            </TableCell>
            <TableCell align="right" width={"10%"}>
              TOTAL
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((row) => {
            const iva = row.iva?.porcentaje ?? 0;
            const ieps = row.ieps?.porcentaje ?? 0;
            const quantity = Number(row.quantity);

            // Si la cantidad es inválida (vacía, 0, NaN), no calcular totales
            const isValidQuantity = !isNaN(quantity) && quantity > 0;

            // Determinar si tiene servicio de asado activo
            const hasGrillingService = row.grilling_config?.has_grilling_service || false;
            const isCarnesProduct = productsService.isCarnesFamily(row);
            const isGrillingServiceRow = row.is_grilling_service || false;
            // Mostrar botón de asado si: tiene has_grilling_service=true O es de familia CARNES
            const canHaveGrillingService = row.has_grilling_service || isCarnesProduct;

            // Precio base + delta de los modificadores elegidos (ver services/pricing)
            const price = getLinePrice(row);

            const lineIva = isValidQuantity ? price * iva * quantity : 0;
            const lineIeps = isValidQuantity ? price * ieps * quantity : 0;
            const subTotal = isValidQuantity ? price * quantity : 0;
            const total = subTotal + lineIva + lineIeps;

            return (
              <TableRow
                key={row.line_uid}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  backgroundColor: hasGrillingService
                    ? "#ff6f00"
                    : isGrillingServiceRow
                    ? "rgba(255, 111, 0, 0.15)"
                    : "inherit",
                  borderLeft: isGrillingServiceRow ? "4px solid #ff6f00" : "none",
                  borderTop: isGrillingServiceRow ? "none" : undefined,
                  "&:hover": {
                    backgroundColor: hasGrillingService
                      ? "#ff8f00"
                      : isGrillingServiceRow
                      ? "rgba(255, 111, 0, 0.25)"
                      : "inherit",
                  }
                }}
              >
                <TableCell
                  align="left"
                  component="th"
                  scope="row"
                  sx={{
                    paddingLeft: isGrillingServiceRow ? '32px' : undefined,
                  }}
                >
                  <Box display="flex" gap={1}>
                    {!row.is_grilling_service && (
                      <>
                        <IconButton
                          aria-label="delete"
                          onClick={() => deleteItem(row)}
                        >
                          <DeleteIcon />
                        </IconButton>
                        {canHaveGrillingService && (
                          <IconButton
                            aria-label="grilling-service"
                            onClick={() => toggleGrillingService(row)}
                            sx={{
                              fontSize: "1.5rem",
                              color: hasGrillingService ? "#fff" : "#ff6f00",
                              "&:hover": {
                                backgroundColor: "rgba(255, 111, 0, 0.1)",
                              }
                            }}
                          >
                            🔥
                          </IconButton>
                        )}
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" component="th" scope="row">
                  <TextField
                    type="number"
                    disabled={row.is_grilling_service || false}
                    // inputProps={{
                    //   inputMode: "numeric",
                    // }}
                    value={row.quantity}
                    onChange={(event) => {
                      let value = event.target.value;
                      const newProducts = products.map(
                        (product: IProduct) => {
                          if (isSameLine(product.line_uid, row.line_uid)) {
                            const numValue = parseFloat(value);

                            // Permitir valores temporales mientras se escribe
                            if (value === '' || value === '0' || value === '0.' || value.startsWith('0.0')) {
                              // Permitir temporalmente para poder escribir 0.5, 0.05, etc.
                              product.quantity = value;
                            } else if (numValue < 0) {
                              // Rechazar valores negativos inmediatamente
                              product.quantity = 1;
                            } else {
                              // Aceptar el valor mientras se escribe
                              if (product.is_bulk === "1") {
                                product.quantity = parseInt(value);
                              } else {
                                product.quantity = value;
                              }
                            }
                          }
                          // Actualizar cantidad del servicio de asado si existe
                          if (product.is_grilling_service && isSameLine(product.parent_line_uid, row.line_uid)) {
                            product.quantity = value;
                          }
                          return product;
                        }
                      );
                      setProducts(newProducts);
                    }}
                    onBlur={(event) => {
                      let value = event.target.value;

                      // Primero calcular la cantidad final del producto padre
                      let finalQuantity: number | string = 1;
                      const numValue = parseFloat(value);

                      if (
                        isNaN(numValue) ||
                        value === '' ||
                        value === '0.' ||
                        numValue < MIN_QTY
                      ) {
                        finalQuantity = 1;
                      } else {
                        if (row.is_bulk === "1") {
                          finalQuantity = parseInt(value);
                        } else {
                          finalQuantity = parseFloat(value);
                        }
                      }

                      // Ahora actualizar todos los productos
                      const newProducts = products.map(
                        (product: IProduct) => {
                          if (isSameLine(product.line_uid, row.line_uid)) {
                            product.quantity = finalQuantity;
                          }
                          // Actualizar cantidad del servicio de asado si existe
                          if (product.is_grilling_service && isSameLine(product.parent_line_uid, row.line_uid)) {
                            product.quantity = finalQuantity;
                          }
                          return product;
                        }
                      );
                      setProducts(newProducts);
                    }}
                  />
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    paddingLeft: isGrillingServiceRow ? '32px' : undefined,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box flex={1}>
                      {isGrillingServiceRow && (
                        <span style={{ marginRight: '8px', color: '#ff6f00', fontWeight: 'bold' }}>
                          ↳ 🔥
                        </span>
                      )}
                      {row.name} <br />

                      {/* Mostrar opciones de cocción y corte para servicios de asado de productos CARNES */}
                      {isGrillingServiceRow && row.parent_product_id && (() => {
                        const parentProduct = products.find((p: IProduct) => isSameLine(p.line_uid, row.parent_line_uid));
                        if (parentProduct?.grilling_config?.cooking_point && parentProduct?.grilling_config?.cut_type) {
                          return (
                            <div style={{ fontSize: '0.85em', color: '#FFFF', marginTop: '4px' }}>
                              <div>• COCCIÓN: {parentProduct.grilling_config.cooking_point}</div>
                              <div>• CORTE: {parentProduct.grilling_config.cut_type}</div>
                              {parentProduct.grilling_config.kitchen_notes && (
                                <div style={{ fontStyle: 'italic', marginTop: '4px' }}>
                                  📝 {parentProduct.grilling_config.kitchen_notes}
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Mostrar opciones de cocción y corte para productos con servicio GRATIS (has_grilling_service=true) */}
                      {(!isGrillingServiceRow && row.has_grilling_service && hasGrillingService && row.grilling_config?.cooking_point && row.grilling_config?.cut_type) ? (
                        <div style={{ fontSize: '0.85em', color: '#FFFF', marginTop: '4px' }}>
                          <div>🔥 ASADO GRATIS</div>
                          <div>• COCCIÓN: {row.grilling_config.cooking_point}</div>
                          <div>• CORTE: {row.grilling_config.cut_type}</div>
                          {row.grilling_config.kitchen_notes && (
                            <div style={{ fontStyle: 'italic', marginTop: '4px' }}>
                              📝 {row.grilling_config.kitchen_notes}
                            </div>
                          )}
                        </div>
                      ) : null}

                      {row.selectedOptions
                        ? row.selectedOptions.split(",").map((e: string) => {
                            return (
                              <div key={e}>
                                - {e} <br />
                              </div>
                            );
                          })
                        : ""}{" "}
                    </Box>

                    {/* Reabrir la ventana de opciones sobre este renglon */}
                    {!isGrillingServiceRow && ((row.option_groups?.length ?? 0) > 0 || (row.options?.length ?? 0) > 0) && (
                      <IconButton
                        size="small"
                        aria-label="edit-product-options"
                        onClick={() => editProductOptions(row)}
                        sx={{
                          color: '#4fc3f7',
                          "&:hover": { backgroundColor: "rgba(79, 195, 247, 0.1)" },
                        }}
                      >
                        ✏️
                      </IconButton>
                    )}

                    {/* Botón de editar opciones de asado para líneas de servicio */}
                    {isGrillingServiceRow && row.parent_product_id && (() => {
                      const parentProduct = products.find((p: IProduct) => isSameLine(p.line_uid, row.parent_line_uid));
                      if (parentProduct?.product_type_id === 41) {
                        return (
                          <IconButton
                            size="small"
                            aria-label="edit-grilling-options"
                            onClick={() => editGrillingOptions(parentProduct)}
                            sx={{
                              color: '#ff6f00',
                              "&:hover": {
                                backgroundColor: "rgba(255, 111, 0, 0.1)",
                              }
                            }}
                          >
                            ✏️
                          </IconButton>
                        );
                      }
                      return null;
                    })()}

                    {/* Botón de editar opciones de asado para productos con servicio GRATIS */}
                    {(!isGrillingServiceRow && row.has_grilling_service && hasGrillingService && row.product_type_id === 41) ? (
                      <IconButton
                        size="small"
                        aria-label="edit-grilling-options-free"
                        onClick={() => editGrillingOptions(row)}
                        sx={{
                          color: '#ff6f00',
                          "&:hover": {
                            backgroundColor: "rgba(255, 111, 0, 0.1)",
                          }
                        }}
                      >
                        ✏️
                      </IconButton>
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell align="right">{price.toFixed(2)}</TableCell>
                <TableCell align="right">{isValidQuantity ? lineIva.toFixed(2) : '-'}</TableCell>
                <TableCell align="right">{isValidQuantity ? lineIeps.toFixed(2) : '-'}</TableCell>
                <TableCell align="right">{isValidQuantity ? total.toFixed(2) : '-'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
